// Sidebar collapse is a desktop-only preference. Below the 768px breakpoint
// `collapsed` doubles as "mobile drawer open" (see left-sidebar-1/index.js
// `mobile-open`), so mobile toggles must never be persisted or restored —
// otherwise a refresh on a phone would reopen the drawer.
//
// THE PREFERENCE IS STORED PER WIDTH BAND, not once for all of desktop
// (Steph, 2026-09-12). Between 768px and the 1280px "wide desktop" line the
// expanded nav is ~205px, which pushes the quotes / orders tables into
// horizontal scroll and clips columns — so that band auto-rails, and the wide
// band starts expanded. A single shared key made one of those two defaults
// unreachable in practice: expand the nav once on a large monitor and the
// saved `false` outranked the auto-rail at every width down to 768px, so a
// smaller window silently got the layout the auto-rail exists to prevent.
//
// Each band now remembers its own choice. A toggle always sticks in the band
// it was made in and never leaks into the other one. The original key stays
// the WIDE band's, so an existing saved preference keeps its meaning on the
// monitor it was almost certainly chosen on.
const SIDEBAR_COLLAPSED_KEY = 'timbercloud-sidebar-collapsed';
const SIDEBAR_COLLAPSED_KEY_COMPACT = 'timbercloud-sidebar-collapsed-compact';
export const SIDEBAR_WIDE_DESKTOP_MIN = 1280;

const isDesktop = () =>
  typeof window !== 'undefined' && window.innerWidth >= 768;
const isWideDesktop = () =>
  typeof window !== 'undefined' && window.innerWidth >= SIDEBAR_WIDE_DESKTOP_MIN;

/** The storage key for the band the window is in right now. */
const bandKey = () =>
  isWideDesktop() ? SIDEBAR_COLLAPSED_KEY : SIDEBAR_COLLAPSED_KEY_COMPACT;

const persistCollapsed = (value) => {
  if (!isDesktop()) return;
  try {
    localStorage.setItem(bandKey(), String(value));
  } catch {}
};

/** Wide desktops start expanded; the compact band auto-rails. */
const defaultDesktopCollapsed = () => !isWideDesktop();

const readStoredCollapsed = () => {
  try {
    const stored = localStorage.getItem(bandKey());
    if (stored === 'true') return true;
    if (stored === 'false') return false;
  } catch {}
  return defaultDesktopCollapsed();
};

export default function config(
  state = {
    name: 'TimberCloud.',
    description: 'Next.js Tailwind CSS admin template',
    url: 'https://d-board-nextjs.mobifica.com',
    layout: 'layout-1',
    collapsed: false,
    // True while a Settings page holds the main nav OPEN — it has become the
    // Settings nav (left-sidebar-1/SettingsNav), which needs its labels. Not a
    // preference and never persisted: it only makes HYDRATE keep it open instead
    // of restoring the saved width-band choice (see SettingsWorkspace).
    settingsRail: false,
    // True while a page holds the main nav on its ICON RAIL for room — the
    // product builder, whose editor needs the width (2026-09-29). Also never
    // persisted: HYDRATE keeps the rail instead of restoring the saved choice, and
    // a toggle the user makes meanwhile is a real choice that ends the hold.
    railHold: false,
    // True while a page hides the app's global top bar to focus on one job — the
    // product builder, whose own header takes its place (2026-09-29). Desktop only
    // (the phone bar carries the menu button) and never persisted.
    focusMode: false,
    rightSidebar: false,
    backdrop: false,
  },
  action
) {
  switch (action.type) {
    case 'SET_CONFIG':
      return {
        ...state,
        ...action.config,
      };
    case 'SET_CONFIG_KEY':
      if (action.key === 'collapsed') {
        persistCollapsed(action.value);
      }
      return {
        ...state,
        [`${action.key}`]: action.value,
      };
    case 'TOGGLE_SIDEBAR':
      persistCollapsed(!state.collapsed);
      return {
        ...state,
        railHold: false,
        collapsed: !state.collapsed,
      };
    case 'HYDRATE_SIDEBAR_COLLAPSED': {
      if (!isDesktop()) return { ...state, collapsed: false };
      return {
        ...state,
        collapsed: state.settingsRail ? false : state.railHold ? true : readStoredCollapsed(),
      };
    }
    default:
      return state;
  }
}
